/**
 * scriptSandbox.js
 *
 * Executes pre-request and test scripts inside a DEDICATED Worker loaded from
 * the static asset `public/scriptSandbox.worker.js`.
 *
 * The worker is loaded from a URL rather than a Blob so it carries its own
 * (absent) CSP instead of inheriting the document's — that is what lets the
 * body use `new Function` without `'unsafe-eval'` anywhere in the app. See the
 * "Worker source" note below.
 *
 * Why a Worker and not the <iframe srcdoc> this used to be (COL-11): the
 * iframe shared the WKWebView content process with the host document, so the
 * 5000 ms "timeout" was a host-thread setTimeout that a `while(true){}` in the
 * frame starved along with the rest of the UI — the timeout never fired and
 * the window froze. A Worker runs on its own thread with its own event loop,
 * so `worker.terminate()` genuinely kills a spinning script and the host timer
 * keeps ticking while it spins. That is the entire point of the change.
 *
 * Returns: Promise<{ variables: {key: value}, envMutations: object, errors: string[], tests: Array,
 *                   requestMutations?: object }>   (requestMutations: untrusted, see utils/scriptRequestMutations)
 */
import { redactWithheldInRequest, redactWithheldInResponse } from './sandboxRequestRedaction';

/** Wall-clock budget for one user script. */
export const SCRIPT_TIMEOUT_MS = 5000;

/**
 * Budget while a `pm.sendRequest` the script asked for is in flight.
 *
 * The 5 s script budget is about a script that will not stop computing; it is
 * the wrong number for one that is legitimately waiting on someone else's
 * token endpoint. The clock is raised for the wait and dropped back to
 * SCRIPT_TIMEOUT_MS the moment the answer lands, so a script cannot buy
 * unlimited CPU time by making one request.
 */
export const SCRIPT_HTTP_TIMEOUT_MS = 30_000;

/**
 * How many `pm.sendRequest` calls one script may make.
 *
 * Enforced HERE and not only in the worker: the worker's limit is a courtesy
 * to the script author, this one is the control. A pre-request script runs on
 * every single send, so an import that fetches a token in a loop would
 * otherwise turn one click into unbounded outbound traffic.
 */
export const MAX_SCRIPT_HTTP_REQUESTS = 5;

// ── Worker source ─────────────────────────────────────────────────────────────
//
// The worker body is NOT in this module any more. It is a static asset,
// `public/scriptSandbox.worker.js`, and the worker is constructed from that
// URL rather than from a Blob.
//
// Why: the body compiles the user's script with `new Function`. A Blob-URL
// worker inherits the creating document's CSP, so `new Function` there is
// governed by the app's `script-src` and throws EvalError unless the whole app
// is opened up with `'unsafe-eval'`. A worker loaded from a real URL derives
// its CSP from its own HTTP response instead — and Tauri only attaches the
// configured CSP to `.html` assets (2.11.1 `src/manager/mod.rs`: `let is_html =
// asset_path.as_ref().ends_with(".html")`; `csp_header` is left None otherwise,
// and `protocol/tauri.rs` only sets the header `if let Some(csp)`). So the
// worker gets no CSP, `new Function` works, and the app's CSP is untouched.
// blob:/data:/about: are the URL kinds that inherit, which is exactly the path
// being abandoned here.
//
// Consequence for tests: WORKER_SOURCE is no longer importable from this
// module. Tests read the shipped asset off disk instead — see
// `src/tests/helpers/sandboxWorkerSource.js`, which is strictly better, since
// they now assert against the exact bytes that ship rather than a copy.

/**
 * URL of the sandbox worker asset.
 *
 * Root-relative via PUBLIC_URL so it resolves the same in `npm start`, in the
 * packaged Tauri app (custom protocol at the bundle root) and in any
 * sub-path deployment. CRA replaces `process.env.PUBLIC_URL` at build time.
 */
export const SANDBOX_WORKER_URL = `${process.env.PUBLIC_URL || ''}/scriptSandbox.worker.js`;

// ── Sandbox execution ─────────────────────────────────────────────────────────

/**
 * 128-bit per-run nonce. The sandbox worker echoes it in SCRIPT_RESULT and this
 * side refuses any result that doesn't carry it, so a result can only come
 * from the run it belongs to.
 */
function newRunNonce() {
  const bytes = new Uint8Array(16);
  const webcrypto = typeof window !== 'undefined' ? window.crypto : undefined;
  if (webcrypto && typeof webcrypto.getRandomValues === 'function') {
    webcrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Build exactly what gets posted into the sandbox worker.
 *
 * COL-02's load-bearing line is the `delete`: a withheld secret's VALUE never
 * crosses into the worker at all. The in-worker guards
 * (`assertNotWithheld`) exist to give the script a message instead of a
 * silent null — they are not the containment. Callers pass whatever variable
 * map they already had; this function is the single choke point where the
 * secret policy is applied, so a caller that forgets to filter its own map
 * still cannot leak, as long as it names the keys.
 *
 * Exported so the payload can be asserted on directly — the worker body itself
 * is not executable under jsdom, so tests run it in a Node `vm` instead (see
 * scriptSandboxPm.test.js).
 */
export function buildSandboxPayload(script, context = {}, nonce = '') {
  const {
    environmentVariables = {},
    localVariables = {},
    request = {},
    response = null,
    withheldKeys = [],
    // `{ key: value }` of withheld secrets (sandboxRequestRedaction's
    // withheldSecretValues). Their values are put back as `{{key}}` in the
    // request snapshot — the resolved URL and `Authorization` header are the
    // other way a value crosses in.
    withheldValues = {},
    sendHttp = null,
  } = context;

  const keys = [...new Set((withheldKeys || []).map(String))];
  const safeEnv = { ...environmentVariables };
  const safeLocals = { ...localVariables };
  // A caller that named a key but still had its value in a map: that value is
  // redacted from the request too, not only deleted from the map.
  const redact = { ...(withheldValues || {}) };
  for (const key of keys) {
    for (const map of [safeEnv, safeLocals]) {
      const v = map[key];
      if (typeof v === 'string' && v && !(key in redact)) redact[key] = v;
    }
    delete safeEnv[key];
    delete safeLocals[key];
  }

  return {
    type: 'RUN_SCRIPT',
    nonce,
    script,
    environmentVariables: safeEnv,
    localVariables: safeLocals,
    request: redactWithheldInRequest(request, redact),
    response: redactWithheldInResponse(response, redact),
    withheldKeys: keys,
    // A caller that supplies no sender gets a sandbox with no network at all,
    // and `pm.sendRequest` says so instead of hanging. The AI dry run is such a
    // caller on purpose — a preview must not make real calls.
    httpEnabled: typeof sendHttp === 'function',
    httpLimit: MAX_SCRIPT_HTTP_REQUESTS,
  };
}

/**
 * Spawn one sandbox worker from the static asset.
 *
 * Split out so tests can stub the spawn without stubbing runScript's protocol.
 *
 * `objectUrl` is retained in the return shape and returned empty: there is no
 * Blob URL to revoke any more, and cleanup() still calls revokeObjectURL only
 * when it is non-empty. Keeping the shape avoids touching the settle/cleanup
 * path in the same change that moves the worker.
 *
 * @returns {{ worker: Worker, objectUrl: string }}
 */
export function spawnSandboxWorker() {
  return { worker: new Worker(SANDBOX_WORKER_URL), objectUrl: '' };
}

/**
 * Run a pre-request or test script in a sandboxed Worker.
 *
 * @param {string} script              - JavaScript source to execute
 * @param {object} context
 * @param {object} context.environmentVariables  - { key: value } map of active env vars
 * @param {object} context.localVariables        - { key: value } map of local (request-scope) vars
 * @param {object} context.request               - { url, method, headers }
 * @param {object|null} context.response         - null for pre-request; response object for tests
 * @param {string[]} context.withheldKeys        - secret variable names the script may not read or write (COL-02)
 * @param {(request: {url: string, method: string, headers: object, body: string|null}) => Promise<object>} [context.sendHttp]
 *        Performs one `pm.sendRequest` on the script's behalf. OMIT IT to give
 *        the script no network: the worker has no fetch/XHR of its own, so
 *        this argument is the entire capability. Pass the app's normal sender
 *        so the user's proxy, client certificates and TLS settings apply.
 *
 * @returns {Promise<{ variables: object, envMutations: object, errors: string[], tests: Array, requestMutations?: object }>}
 */
export async function runScript(script, context = {}) {
  if (!script || typeof script !== 'string' || script.trim() === '') {
    return { variables: {}, envMutations: {}, errors: [], tests: [] };
  }

  const { sendHttp } = context;

  return new Promise((resolve) => {
    const runNonce = newRunNonce();

    let worker = null;
    let objectUrl = '';
    let timer = null;
    let settled = false;
    let httpCount = 0;
    /**
     * Calls still waiting on a server. The budget drops back to the script
     * clock only when this hits zero — with three parallel sendRequests, the
     * first (fast) answer must not put the other two on a 5 s deadline.
     */
    let httpInFlight = 0;

    /** (Re)arm the watchdog. Called with the longer budget while HTTP is out. */
    function arm(ms) {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(() => {
        settle({
          variables: {},
          envMutations: {},
          errors: [`Script execution timed out (${ms}ms)`],
          tests: [],
        });
      }, ms);
    }

    function cleanup() {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      if (worker) {
        // COL-11: this is the line the whole change exists for. terminate() on
        // a dedicated worker tears down its thread whatever it is doing —
        // including a `while(true){}` that no cooperative signal could ever
        // interrupt. The old iframe had no equivalent: removing the element
        // does not stop script already spinning in the shared content process.
        try { worker.terminate(); } catch { /* already gone */ }
        worker = null;
      }
      if (objectUrl) {
        try { URL.revokeObjectURL(objectUrl); } catch { /* nothing to revoke */ }
        objectUrl = '';
      }
    }

    function settle(result) {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    }

    try {
      ({ worker, objectUrl } = spawnSandboxWorker());
    } catch (err) {
      resolve({
        variables: {},
        envMutations: {},
        errors: [`Script sandbox unavailable: ${err?.message || String(err)}`],
        tests: [],
      });
      return;
    }

    // Timeout: kill hung scripts after SCRIPT_TIMEOUT_MS. Unlike the iframe
    // version, this timer runs on a thread the script cannot starve, and
    // settle() -> cleanup() actually stops the script.
    arm(SCRIPT_TIMEOUT_MS);

    /** Answer one pm.sendRequest on the worker's behalf. */
    async function serveHttp(id, request) {
      let payload;
      if (httpCount >= MAX_SCRIPT_HTTP_REQUESTS) {
        payload = { error: `pm.sendRequest: too many requests from one script (limit ${MAX_SCRIPT_HTTP_REQUESTS})` };
      } else {
        httpCount += 1;
        httpInFlight += 1;
        try {
          payload = { response: await sendHttp(request) };
        } catch (err) {
          payload = { error: err?.message || String(err) };
        } finally {
          httpInFlight -= 1;
        }
      }
      // settle() has already torn the worker down on a timeout; posting into a
      // terminated worker throws and would reject nothing that is listening.
      if (settled || !worker) return;
      if (httpInFlight === 0) arm(SCRIPT_TIMEOUT_MS);
      try {
        worker.postMessage({ type: 'SCRIPT_HTTP_RESULT', nonce: runNonce, id, ...payload });
      } catch { /* worker gone between the checks */ }
    }

    // Bound to the Worker, NOT to window: no other frame or document can
    // deliver here, so a forged SCRIPT_RESULT has nowhere to arrive.
    worker.onmessage = (e) => {
      const data = e.data;
      if (!data) return;
      // Only replies to THIS run, whatever their kind.
      if (data.nonce !== runNonce) return;

      if (data.type === 'SCRIPT_HTTP_REQUEST') {
        if (typeof sendHttp !== 'function') return;
        // The script is now waiting on someone else's server, not on itself.
        arm(SCRIPT_HTTP_TIMEOUT_MS);
        serveHttp(data.id, data.request || {});
        return;
      }

      if (data.type !== 'SCRIPT_RESULT') return;
      const result = {
        variables: data.variables || {},
        envMutations: data.envMutations || {},
        errors: data.errors || [],
        tests: data.tests || [],
      };
      // req.setHeader() & co. from a pre-request script. Passed through as the
      // worker sent it — it is UNTRUSTED and is validated where it is applied
      // (utils/scriptRequestMutations), which is the only place that uses it.
      if (data.requestMutations) result.requestMutations = data.requestMutations;
      settle(result);
    };

    worker.onerror = (e) => {
      settle({
        variables: {},
        envMutations: {},
        errors: [e?.message || 'Script sandbox error'],
        tests: [],
      });
    };

    worker.postMessage(buildSandboxPayload(script, context, runNonce));
  });
}
